import { useGoogleLogin } from '@react-oauth/google';
import { FcGoogle } from 'react-icons/fc';
import { useUser } from '../contexts/UserContext';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ParticleBackground from '../components/ParticleBackground';
import '../styles/JoinOrganization.scss';
import { API_BASE } from '../config/api';
import { useTranslation } from '../contexts/TranslationContext';


const JoinOrganization = () => {
  const { refreshUser } = useUser(); // ✅ MOVE HERE
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Invite validation state
  const [inviteInfo, setInviteInfo] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState('');

  // UI mode: 'choose' | 'register' | 'login'
  const [mode, setMode] = useState('choose');

  // Form fields
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials' | 'otp'

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const inviteGoogleAuth = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      setSuccess('');

      try {
        const response = await fetch(`${API_BASE}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            googleAccessToken: tokenResponse.access_token,
            expectedEmail: inviteInfo?.email,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (data.code === 'INVITE_EMAIL_MISMATCH') {
            setError(t('inviteGoogleEmailMismatch'));
            return;
          }
          throw new Error(data.message || t('inviteGoogleAuthFailed'));
        }

        await handleAcceptAfterLogin(data.token, t('inviteGoogleJoinSuccessful', { orgName: inviteInfo?.orgName || '' }));
      } catch (_) {
        setError(t('inviteGoogleAuthFailed'));
      } finally {
        setLoading(false);
      }
    },
    onError: () => setError(t('inviteGoogleAuthFailedShort')),
  });

  // ── Step 1: Validate the token on mount ────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValidationError(t('inviteMissingToken'));
      return;
    }

    const validateToken = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/org/invite/${token}`);
        const data = await res.json();

        if (!res.ok) {
          setValidationError(data.error || t('inviteInvalidLink'));
        } else {
          setInviteInfo(data.invite);
          // Pre-fill the login form's email (register form reads inviteInfo.email directly)
          setLoginEmail(data.invite.email);
        }
      } catch (_) {
        setValidationError(t('inviteCouldNotConnect'));
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // ── Shared: persist JWT and redirect ──────────────────────────────────────
  const finalizeLogin = async (jwt, message) => {
    localStorage.setItem('token', jwt);

    await refreshUser(); // ✅ now valid

    setSuccess(message || t('inviteWelcomeRedirect', { orgName: inviteInfo?.orgName || '' }));
    setTimeout(() => navigate('/'), 2000);
  };

  // ── Accept invite (works for both new and existing logged-in users) ────────
  const acceptInvite = async (jwt) => {
    const headers = { 'Content-Type': 'application/json' };
    if (jwt) headers['x-auth-token'] = jwt;

    const res = await fetch(`${API_BASE}/api/org/accept-invite`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token })
    });
    return res.json();
  };

  // ── REGISTER flow ──────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError(t('invitePasswordsDoNotMatch'));
      return;
    }
    if (password.length < 6) {
      setError(t('invitePasswordMinLength'));
      return;
    }
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/org/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, name, password })
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.requiresLogin) {
          setError(data.error);
          setLoginEmail(inviteInfo.email);
          setMode('login');
        } else {
          setError(data.error || t('inviteRegistrationFailed'));
        }
        return;
      }

      finalizeLogin(data.token, data.message);
    } catch (_) {
      setError(t('inviteNetworkError'));
    } finally {
      setLoading(false);
    }
  };

  // ── LOGIN flow ─────────────────────────────────────────────────────────────
  const handleLoginCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || t('inviteInvalidCredentials'));
        return;
      }

      // Login sends OTP — move to OTP step
      if (data.message && data.message.toLowerCase().includes('otp')) {
        setLoginStep('otp');
      } else if (data.token) {
        // Directly logged in (Google or password-reset bypass)
        await handleAcceptAfterLogin(data.token);
      }
    } catch (_) {
      setError(t('inviteNetworkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoginOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, otp: loginOtp })
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || t('inviteInvalidOtp'));
        return;
      }

      await handleAcceptAfterLogin(data.token);
    } catch (_) {
      setError(t('inviteNetworkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAfterLogin = async (jwt) => {
    const data = await acceptInvite(jwt);
    if (data.success) {
      finalizeLogin(data.token || jwt, data.message);
    } else {
      // Already a member or other non-fatal issue
      setError(data.error || t('inviteCouldNotAccept'));
      if (data.error?.toLowerCase().includes('already a member')) {
        localStorage.setItem('token', jwt);
        setTimeout(() => navigate('/'), 2000);
      }
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────

  if (validating) {
    return (
      <div className="join-page">
        <ParticleBackground />
        <div className="join-card">
          <div className="join-logo">FORTEXA</div>
          <div className="join-loading">
            <div className="spinner" />
            <p>{t('inviteValidating')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="join-page">
        <ParticleBackground />
        <div className="join-card">
          <div className="join-logo">FORTEXA</div>
          <div className="join-error-box">
            <div className="join-error-icon">✕</div>
            <h2>{t('inviteInvalidTitle')}</h2>
            <p>{validationError}</p>
            <button className="join-btn" onClick={() => navigate('/')}>{t('inviteGoHomepage')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="join-page">
      <ParticleBackground />
      <div className="join-card">
        <div className="join-logo">FORTEXA</div>

        {/* Invite Summary Banner */}
        <div className="join-invite-banner">
          <p className="join-invite-label">{t('inviteSummaryLabel')}</p>
          <h2 className="join-org-name">{inviteInfo.orgName}</h2>
          <span className="join-role-badge">{inviteInfo.role.toUpperCase()}</span>
        </div>

        {success && <div className="join-success">{success}</div>}
        {error && <div className="join-error">{error}</div>}

        {/* ── Mode: Choose ── */}
        {mode === 'choose' && (
          <div className="join-choose">
            <p className="join-subtitle">{t('inviteHowWouldYouLikeToJoin')}</p>
            <button className="join-btn join-btn--primary" onClick={() => setMode('register')}>
              {t('inviteCreateNewAccount')}
            </button>
            <button className="join-btn join-btn--secondary" onClick={() => setMode('login')}>
              {t('inviteLogInExistingAccount')}
            </button>
          </div>
        )}

        {/* ── Mode: Register ── */}
        {mode === 'register' && (
          <form className="join-form" onSubmit={handleRegister}>
            <h3>{t('inviteCreateYourAccount')}</h3>
            <p className="join-email-notice">{t('inviteJoiningAs', { email: inviteInfo.email })}</p>

            <button type="button" className="join-google-btn" onClick={() => inviteGoogleAuth()} disabled={loading}>
              <FcGoogle size={22} />
              <span>{t('inviteContinueWithGoogle')}</span>
            </button>

            <div className="join-divider"><span>{t('or')}</span></div>

            <div className="join-field">
              <label>{t('inviteFullName')}</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('inviteFullNamePlaceholder')}
                required
                autoFocus
              />
            </div>
            <div className="join-field">
              <label>{t('invitePassword')}</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={t('inviteCreatePasswordPlaceholder')}
                required
              />
            </div>
            <div className="join-field">
              <label>{t('inviteConfirmPassword')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder={t('inviteRepeatPasswordPlaceholder')}
                required
              />
            </div>

            <button type="submit" className="join-btn join-btn--primary" disabled={loading}>
              {loading ? t('inviteCreatingAccount') : t('inviteCreateAccountAndJoin')}
            </button>
            <button type="button" className="join-btn--link" onClick={() => setMode('choose')}>
              {t('inviteBack')}
            </button>
          </form>
        )}

        {/* ── Mode: Login ── */}
        {mode === 'login' && loginStep === 'credentials' && (
          <form className="join-form" onSubmit={handleLoginCredentials}>
            <h3>{t('inviteLogInToJoin')}</h3>

            <button type="button" className="join-google-btn" onClick={() => inviteGoogleAuth()} disabled={loading}>
              <FcGoogle size={22} />
              <span>{t('inviteContinueWithGoogle')}</span>
            </button>

            <div className="join-divider"><span>{t('or')}</span></div>
            <div className="join-field">
              <label>{t('email')}</label>
              <input
                type="email"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                placeholder={t('emailPlaceholder')}
                required
              />
            </div>
            <div className="join-field">
              <label>{t('password')}</label>
              <input
                type="password"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                placeholder={t('passwordPlaceholder')}
                required
              />
            </div>

            <button type="submit" className="join-btn join-btn--primary" disabled={loading}>
              {loading ? t('inviteLoggingIn') : t('inviteLogInAndJoin')}
            </button>
            <button type="button" className="join-btn--link" onClick={() => setMode('choose')}>
              {t('inviteBack')}
            </button>
          </form>
        )}

        {/* ── Mode: Login OTP ── */}
        {mode === 'login' && loginStep === 'otp' && (
          <form className="join-form" onSubmit={handleLoginOtp}>
            <h3>{t('inviteEnterVerificationCode')}</h3>
            <p className="join-subtitle">{t('inviteWeSentCode', { email: loginEmail })}</p>
            <div className="join-field">
              <label>{t('inviteOtpCode')}</label>
              <input
                type="text"
                value={loginOtp}
                onChange={e => setLoginOtp(e.target.value)}
                placeholder={t('inviteOtpPlaceholder')}
                maxLength={6}
                required
                autoFocus
              />
            </div>
            <button type="submit" className="join-btn join-btn--primary" disabled={loading}>
              {loading ? t('inviteVerifying') : t('inviteVerifyAndJoin')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default JoinOrganization;
